import { memo, useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from '@/i18n';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useSelectSearch,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { FuelTypeOption } from '@/hooks/useFuelTypes';

function FloatingSelectValue({
  label,
  hasValue,
  placeholder,
  children,
}: {
  label: string;
  hasValue: boolean;
  placeholder?: string;
  children?: ReactNode;
}) {
  const { searchTerm } = useSelectSearch();
  const isTyping = searchTerm.length > 0;
  const showValue = hasValue || isTyping;
  const labelText = showValue ? label : (placeholder ?? label);
  const displayText = isTyping ? searchTerm : null;

  return (
    <div className="relative w-full">
      <div
        className={cn(
          'pointer-events-none absolute left-0 origin-left text-[#666666] transition-all duration-200',
          showValue
            ? 'top-0 text-[0.8125rem] leading-4'
            : 'top-1/2 -translate-y-1/2 text-base leading-6'
        )}
      >
        {labelText}
      </div>
      {showValue ? (
        <div className="flex items-start pt-5 text-base leading-6 text-[#292929]">
          {isTyping ? (
            <span className="leading-6">{displayText}</span>
          ) : (
            <SelectValue placeholder={placeholder ?? label}>{children}</SelectValue>
          )}
        </div>
      ) : null}
    </div>
  );
}

export interface FuelExpense {
  id?: number;
  fuelTypeId: string;
  fuelTypeName: string;
  carModel: string;
  amount: string;
  currency: string;
}

interface FuelExpenseFormProps {
  onSave: (expense: FuelExpense) => void;
  onCancel: () => void;
  initialData?: FuelExpense;
  isLoading?: boolean;
  fuelTypes: FuelTypeOption[];
  fuelTypesLoading?: boolean;
}

function FuelExpenseFormComponent({
  onSave,
  onCancel,
  initialData,
  isLoading = false,
  fuelTypes,
  fuelTypesLoading = false,
}: FuelExpenseFormProps) {
  const t = useTranslations('expenses');

  const [formData, setFormData] = useState<FuelExpense>({
    id: initialData?.id,
    fuelTypeId: initialData?.fuelTypeId || '',
    fuelTypeName: initialData?.fuelTypeName || '',
    carModel: initialData?.carModel || '',
    amount: initialData?.amount || '',
    currency: initialData?.currency || 'GEL',
  });

  const handleSave = () => {
    if (!formData.fuelTypeId || !formData.amount) return;
    onSave(formData);
  };

  const isValid = formData.fuelTypeId !== '' && formData.amount !== '';

  const fuelTypeItems = useMemo(() => fuelTypes ?? [], [fuelTypes]);

  const selectedFuelTypeLabel = formData.fuelTypeName;

  return (
    <div className="rounded-2xl border border-[#ebebeb] bg-white p-5">
      {/* Header with icon and title */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full items-center gap-3 sm:w-auto">
          <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-[#f9f9f9] p-2">
            <img src="/assets/icons/gas-station.svg" alt="" className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-[18px] leading-7 font-medium text-[#292929]">{t('fuel')}</h3>
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isLoading}
            className="h-auto w-full rounded-[1.875rem] px-3.5 py-2.5 text-sm font-medium text-black hover:bg-[#f9f9f9] sm:w-auto"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || isLoading}
            className={`h-auto w-full rounded-[1.875rem] px-3.5 py-2.5 text-sm font-medium transition-all sm:w-auto ${
              isValid && !isLoading
                ? 'bg-[#242424] text-white hover:bg-[#1a1a1a]'
                : 'bg-[#f9f9f9] text-[#bbbbbb]'
            }`}
          >
            {t('save')}
          </Button>
        </div>
      </div>

      {/* Divider */}
      <div className="mb-4 h-px bg-[#f0f0f0]" />

      {/* Fuel Type Dropdown */}
      <div className="mb-6">
        <div className="px-1 pb-3">
          <label className="text-sm font-medium text-[#292929]">{t('fuelType')}</label>
        </div>
        <Select
          value={formData.fuelTypeId}
          onValueChange={(value) => {
            const selected = fuelTypeItems.find((type) => type.id === value);
            setFormData({
              ...formData,
              fuelTypeId: value,
              fuelTypeName: selected?.name ?? '',
            });
          }}
          disabled={fuelTypesLoading}
        >
          <SelectTrigger
            variant="formField"
            showIcon={false}
            className="h-12 w-full rounded-2xl border border-[#ebebeb] bg-white px-4 py-2 transition-colors hover:border-[#292929] focus:border-[#292929] data-[state=open]:border-[#292929]"
          >
            <div className="relative flex h-full w-full items-start justify-between">
              <div className="relative flex h-full flex-1 items-center">
                <FloatingSelectValue
                  label={t('fuelType')}
                  placeholder={t('selectFuelType')}
                  hasValue={Boolean(formData.fuelTypeId)}
                >
                  {selectedFuelTypeLabel || t('selectFuelType')}
                </FloatingSelectValue>
              </div>
              <img src="/assets/icons/chevron-down.svg" alt="" className="h-6 w-6" />
            </div>
          </SelectTrigger>
          <SelectContent className="rounded-2xl border-[#ebebeb]">
            {fuelTypeItems.map((type) => (
              <SelectItem key={type.id} value={type.id} className="text-base leading-6">
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Car Model Input */}
      <div className="mb-6">
        <div className="px-1 pb-3">
          <label className="text-sm font-medium text-[#292929]">{t('carModel')}</label>
        </div>
        <div className="relative">
          <div className="flex h-14 items-center rounded-2xl border border-[#ebebeb] bg-white px-4 py-2 transition-colors focus-within:border-[#292929] hover:border-[#292929]">
            <div className="relative w-full">
              <input
                value={formData.carModel}
                onChange={(e) => setFormData({ ...formData, carModel: e.target.value })}
                placeholder=" "
                className="peer h-10 w-full border-none bg-transparent p-0 pt-4 text-base leading-6 text-[#292929] outline-none"
              />
              <label className="pointer-events-none absolute left-0 origin-left text-[#666666] transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:leading-6 peer-focus:top-0 peer-focus:translate-y-0 peer-focus:text-[0.8125rem] peer-focus:leading-4 peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[0.8125rem] peer-[:not(:placeholder-shown)]:leading-4">
                {t('carModelPlaceholder')}
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Fuel Amount */}
      <div className="flex flex-col">
        <div className="px-1 pb-3">
          <label className="text-sm font-medium text-[#292929]">{t('fuelAmount')}</label>
        </div>
        <div className="relative">
          <div className="flex h-14 items-center rounded-2xl border border-[#ebebeb] bg-white px-4 py-2 transition-colors focus-within:border-[#292929] hover:border-[#292929]">
            <div className="relative w-full">
              <input
                value={formData.amount}
                onChange={(e) => {
                  const raw = e.target.value.replace(',', '.');
                  if (raw === '' || /^\d*(?:\.\d*)?$/.test(raw)) {
                    setFormData({ ...formData, amount: raw });
                  }
                }}
                placeholder=" "
                inputMode="decimal"
                className="peer h-10 w-full border-none bg-transparent p-0 pt-4 text-base leading-6 text-[#292929] outline-none"
              />
              <label className="pointer-events-none absolute left-0 origin-left text-[#666666] transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:leading-6 peer-focus:top-0 peer-focus:translate-y-0 peer-focus:text-[0.8125rem] peer-focus:leading-4 peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[0.8125rem] peer-[:not(:placeholder-shown)]:leading-4">
                {t('liters')}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const FuelExpenseForm = memo(FuelExpenseFormComponent);

export default FuelExpenseForm;
